// services/simulationService.js
import { $fetch } from 'utils/fetch'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'

/**
 * Starts a new simulation run with test profiles
 * @param {Object} params
 * @param {string} params.pathway_id - The ID of the pathway to test
 * @param {number} params.version_number - The version number of the pathway
 * @param {number} params.max_turns - Maximum number of turns in the conversation
 * @param {Array} params.personas - Array of personas to use for testing (max 5)
 * @param {Array} params.request_data - Request data for the pathway
 * @param {string} params.start_node_id - ID of the node to start from
 * @returns {Promise} - Promise with the simulation data
 * @throws {Error} If more than 5 personas are provided
 */
export const runSimulation = async ({
  pathway_id,
  version_number,
  max_turns,
  advanced_instructions,
  personas,
  request_data,
  start_node_id,
}) => {
  if (personas.length > 5) {
    throw new Error('Maximum of 5 test profiles allowed per simulation')
  }

  const token = getAuthToken()
  
  const response = await $fetch('/v1/pathway/simulations/run', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token,
    },
    body: JSON.stringify({
      pathway_id,
      version_number,
      max_turns,
      advanced_instructions,
      personas,
      request_data,
      start_node_id,
    }),
  })
  
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to run simulation')
  }
  
  return response.data
}

/**
 * Gets the current status of a simulation
 * @param {string} simulation_id - The ID of the simulation
 * @returns {Promise} - Promise with the simulation data
 */
export const getSimulation = async (simulation_id) => {
  const token = getAuthToken()
  
  const response = await $fetch(`/v1/pathway/simulations/${simulation_id}`, {
    headers: {
      'Authorization': token,
    },
  })
  
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to get simulation')
  }
  
  return response.data
}

/**
 * Continue a simulation with additional turns
 * @param {string} simulation_id - The ID of the simulation
 * @param {string} persona_id - The ID of the persona to continue
 * @param {number} additional_turns - Number of additional turns
 * @returns {Promise} - Promise with the updated simulation data
 */
export const continueSimulation = async (simulation_id, persona_id, additional_turns = 5) => {
  const token = getAuthToken()
  
  const response = await $fetch(`/v1/pathway/simulations/${simulation_id}/continue`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token,
    },
    body: JSON.stringify({
      call_id: persona_id,
      additional_turns,
    }),
  })
  
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to continue simulation')
  }
  
  return response.data
}